import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useUserData } from '@/libs/fleet-api/hooks';
import { useDownloadPODocument } from '@/libs/fleet-api/services/print-disbursement';
import { usePrintPurchaseOrder } from '@/libs/fleet-api/hooks/purchase-order';
import {
	PrintVerificationSearchPOOrderParam,
	usePrintVerificationSearchPOOptionQuery,
	usePrintVerificationSearchPOOrderQuery,
	useViewDocumentTemplateMutation,
} from '@/libs/fleet-api/services/print-verification';
import { FileSearchOutlined, LoadingOutlined } from '@ant-design/icons';
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Table,
} from 'antd';
import Checkbox from 'antd/es/checkbox/Checkbox';
import dayjs from 'dayjs';
import { FC, useEffect, useMemo, useState } from 'react';

const VerifyDisbursementPOListPage: FC<{
	mppId: number;
	initialApplicationIds?: string[];
}> = ({ mppId, initialApplicationIds }) => {
	const { navigate } = useApp();
	const {
		openCustomerDetail,
		openCustomerGroupDetail,
		openDocumentModal,
		openPreviewDocumentModal,
	} = useModals();
	const userData = useUserData();
	const { groupName } = userData ?? {};

	const [financingProductId, setFinancingProductId] = useState<number>();
	const { form, fieldName, value } = useForm<{
		searchType: string;
		searchParam: PrintVerificationSearchPOOrderParam;
	}>();

	const searchOptionQuery = usePrintVerificationSearchPOOptionQuery();
	const {
		fcohStatusOptions = [],
		purchaseOrderStatusOptions = [],
		searchByOptions = [],
	} = searchOptionQuery.data?.data ?? {};

	const defaultSeachParams: PrintVerificationSearchPOOrderParam = {
		mppId,
		pagination: {
			pageNumber: 1,
			pageSize: 10,
		},
	};

	const [searchParam, setSearchParam] =
		useState<PrintVerificationSearchPOOrderParam>(defaultSeachParams);

	const { data, isFetching } =
		usePrintVerificationSearchPOOrderQuery(searchParam);

	const { items = [], mainInformation } = data?.data ?? {};
	const { mppNo, customerId, customerName, bmppGroupId, bmppGroupName } =
		mainInformation ?? {};

	const dataSource = useMemo(() => items.map((item, key) => ({ ...item, key })), [items]);
	const { disbursementId } =
		dataSource?.find?.((d) => d.financingProduct.id === financingProductId) ??
		{};

	useEffect(() => {
		if (initialApplicationIds?.length && dataSource.length && !financingProductId) {
			const match = dataSource.find((d) =>
				d.applicationId && initialApplicationIds.includes(d.applicationId),
			);
			if (match) setFinancingProductId(match.financingProduct.id);
		}
	}, [dataSource, financingProductId, initialApplicationIds]);

	const selectedDocNo = useMemo(() => {
		if (!financingProductId) return undefined;
		return dataSource.find(
			(d) => d.financingProduct.id === financingProductId,
		)?.disbursementDocumentNo;
	}, [financingProductId, dataSource]);

	const selectedApplicationIds = useMemo(() => {
		if (!selectedDocNo) return [];
		return dataSource
			.filter((d) => d.disbursementDocumentNo === selectedDocNo && d.applicationId && d.applicationId !== '-')
			.map((d) => d.applicationId as string);
	}, [selectedDocNo, dataSource]);

	const incompleteDocGroups = useMemo(() => {
		const groups: Record<string, typeof dataSource> = {};
		for (const item of dataSource) {
			const docNo = item.disbursementDocumentNo;
			if (docNo) {
				(groups[docNo] ??= []).push(item);
			}
		}
		const incomplete = new Set<string>();
		for (const [docNo, items] of Object.entries(groups)) {
			if (items.some((i) => !i.applicationId || !i.agreementNo)) {
				incomplete.add(docNo);
			}
		}
		return incomplete;
	}, [dataSource]);

	const downloadPODocument = useDownloadPODocument();
	const printPurchaseOrderMutation = usePrintPurchaseOrder();
	const viewDocumentTemplateMutation = useViewDocumentTemplateMutation();

	const renderSearchType = () => {
		switch (value?.searchType) {
			case 'simulation-no':
			case 'po-no':
			case 'application-id':
			case 'agreement-no':
			case 'plakat-no':
			case 'supplier-name':
			case 'application-status':
				return (
					<Form.Item name={fieldName(['searchParam', 'search', 'value'])}>
						<Input />
					</Form.Item>
				);
			case 'po-date':
				return (
					<Form.Item
						name={fieldName(['searchParam', 'purchaseOrderDate'])}
						getValueProps={(value) => ({
							value: value && dayjs(value),
						})}
						normalize={(value) => value && dayjs(value).utc(true).toISOString()}
					>
						<DatePicker format={'DD/MM/YYYY'} />
					</Form.Item>
				);
			case 'fcoh-verification-status':
				return (
					<Form.Item
						name={fieldName(['searchParam', 'fcohVerificationStatus'])}
					>
						<Select
							options={fcohStatusOptions.map((option) => {
								return {
									label: option.value,
									value: option.id,
								};
							})}
							style={{ width: '100%' }}
							allowClear
						/>
					</Form.Item>
				);
			case 'po-status':
				return (
					<Form.Item name={fieldName(['searchParam', 'purchaseOrderStatus'])}>
						<Select
							options={purchaseOrderStatusOptions.map((option) => {
								return {
									label: option.value,
									value: option.id,
								};
							})}
							style={{ width: '100%' }}
							allowClear
						/>
					</Form.Item>
				);
			default:
				return (
					<Form.Item>
						<Input disabled />
					</Form.Item>
				);
		}
	};

	return (
		<div>
			<FleetTitle style={{ marginBottom: 20 }}>
				Verifikasi dan Upload Dokumen Kesepakatan Realisasi Pencairan Pembiayaan
			</FleetTitle>

			<DataRow
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (!customerId) return;
									openCustomerDetail(String(customerId));
								}}
							>
								{customerName}
							</a>,
						],
						[
							'Group Customer BMPP',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (!bmppGroupId) return;
									openCustomerGroupDetail(bmppGroupId);
								}}
							>
								{bmppGroupName}
							</a>,
						],
					],
					[
						[
							'No MPP',
							mppNo && mppId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('MPPPage', {
											mppId,
											viewOnly: true,
											onBack: () => {
												navigate('VerifyDisbursementPOListPage', {
													mppId,
												});
											},
										});
									}}
								>
									{mppNo}
								</a>
							) : (
								'-'
							),
						],
						groupName !== 'FCOH'
							? [
								'History PO',
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('VerificationPOHistory', {
											id: mppId,
											onBack: () => {
												navigate('VerifyDisbursementPOListPage', {
													mppId,
												});
											},
										});
									}}
								>
									View
								</a>,
							]
							: undefined,
					],
				]}
			/>

			<Divider />

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={(values) => {
					if (values.searchType) {
						if (
							[
								'simulation-no',
								'po-no',
								'application-id',
								'agreement-no',
								'plakat-no',
								'supplier-name',
								'application-status',
							].includes(values.searchType)
						) {
							values.searchParam.search!.by = values.searchType;
						}

						setSearchParam({
							...values.searchParam,
							mppId,
							pagination: {
								pageNumber: 1,
								pageSize: 10,
							},
						});
					} else {
						setSearchParam(defaultSeachParams);
					}
				}}
			>
				<Row gutter={[20, 20]}>
					<Col flex="none">
						<Form.Item
							name={fieldName('searchType')}
							label={<span>Kata Kunci</span>}
							labelCol={{ flex: 'auto', style: { width: 150 } }}
							wrapperCol={{ flex: 'none', style: { width: 250 } }}
						>
							<Select
								loading={searchOptionQuery.isLoading}
								disabled={isFetching}
								placeholder="Search By"
								options={searchByOptions.map((option) => {
									return {
										label: option.value,
										value: option.id,
									};
								})}
								allowClear
								style={{
									display: 'block',
									width: '100%',
								}}
								onSelect={() => {
									form.resetFields([fieldName('searchParam')]);
								}}
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">{renderSearchType()}</Col>
					<Col>
						<Form.Item>
							<Button
								block
								loading={isFetching}
								type="primary"
								htmlType="submit"
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Divider />
			<Table
				bordered
				scroll={{ x: 'max-content' }}
				loading={isFetching}
				dataSource={dataSource}
				columns={[
					{
						title: 'No',
						align: 'center',
						fixed: 'left',
						width: 1,
						render: (_, __, index) => index + 1,
					},
					{
						title: 'No. Simulasi',
						render: (_, record) =>
							record.simulationNo ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('SimulationPage', {
											simulationId: record.simulationId,
											showResult: true,
											viewOnly: true,
											onBack: () => {
												navigate('VerifyDisbursementPOListPage', {
													mppId,
												});
											},
										});
									}}
									style={{ cursor: 'pointer' }}
								>
									{record.simulationNo}
								</a>
							) : (
								'-'
							),
					},
					{
						title: 'No. PO',
						render: (_, record) =>
							record.purchaseOrderNo ? (
								<a
									href="#"
									onClick={async (e) => {
										e.preventDefault();
										const documents =
											await printPurchaseOrderMutation.mutateAsync({
												purchaseOrderIds: [record.purchaseOrderId],
											});

										openDocumentModal(documents);
									}}
								>
									{record.purchaseOrderNo}
								</a>
							) : (
								'-'
							),
					},
					{
						title: 'No. Dokumen Realisasi',
						render: (_, record) => record.disbursementDocumentNo ?? '-',
					},
					{
						title: 'Skema Pembiayaan',
						render: (_, record) => record.financingProduct?.value ?? '-',
					},
					{
						title: 'Tanggal Cetak Dokumen',
						render: (_, record) =>
							record.disbursementDate
								? dayjs(record.disbursementDate).format('DD/MM/YYYY')
								: '-',
					},
					{
						title: 'Application ID',
						render: (_, record) => record.applicationId ?? '-',
					},
					{
						title: 'Agreement No',
						render: (_, record) => record.agreementNo ?? '-',
					},
					{
						title: 'Supplier Name',
						render: (_, record) => record.supplierName ?? '-',
					},
					{
						title: 'Contact Person',
						render: (_, record) => record.supplierPicName ?? '-',
					},
					{
						title: 'Supplier Phone',
						render: (_, record) => record.supplierPhone ?? '-',
					},
					{
						title: 'Asset',
						render: (_, record) => record.asset ?? '-',
					},
					{
						title: 'Jumlah Unit ',
						align: 'center',
						render: (_, record) => record.unit ?? '-',
					},
					{
						title: 'PO Date',
						render: (_, record) =>
							record.purchaseOrderDate
								? dayjs(record.purchaseOrderDate).format('DD/MM/YYYY')
								: '-',
					},
					{
						title: 'View PO',
						align: 'center',
						fixed: 'right',
						render: (_, record) => {
							if (!record.purchaseOrderId) {
								return <FileSearchOutlined style={{ color: '#bababa' }} />;
							}

							return (
								<a
									href="#"
									onClick={async (e) => {
										e.preventDefault();
										if (downloadPODocument.isPending) return;

										const res = await downloadPODocument.mutateAsync([
											record.purchaseOrderId,
										]);

										const [data] = res.data ?? [];
										if (!data) return;

										const link = document.createElement('a');

										link.href = data.url;
										link.click();
										document.body.removeChild(link);
									}}
								>
									{downloadPODocument.isPending &&
										downloadPODocument.variables.includes(
											record.purchaseOrderId,
										) ? (
										<LoadingOutlined />
									) : (
										<FileSearchOutlined />
									)}
								</a>
							);
						},
					},
					{
						title: 'Status Verifikasi FCOH',
						align: 'center',
						render: (_, record) => record.verificationStatus ?? '-',
					},
					{
						title: 'Status Aplikasi',
						align: 'center',
						render: (_, record) => record.applicationStatus ?? '-',
					},
					{
						title: 'View Tagihan',
						align: 'center',
						fixed: 'right',
						render: (_, record) => {
							if (!record.financingProduct?.id || !mppId) {
								return <FileSearchOutlined style={{ color: '#bababa' }} />;
							}

							const isLoading =
								viewDocumentTemplateMutation.isPending &&
								viewDocumentTemplateMutation.variables?.financingProductId ===
								record.financingProduct.id;

							return (
								<a
									href="#"
									onClick={async (e) => {
										e.preventDefault();
										if (isLoading) return;

										const res = await viewDocumentTemplateMutation.mutateAsync({
											financingProductId: record.financingProduct.id,
											mppId,
										});

										const url = res.data.map((i) => i.signedUrl);
										if (!url.length) return;

										openPreviewDocumentModal({
											url,
											allowDownload: false,
											buttonPosition: 'top',
										});
									}}
								>
									{isLoading ? <LoadingOutlined /> : <FileSearchOutlined />}
								</a>
							);
						},
					},
					{
						title:
							groupName === 'FCDVS' ? 'Upload Dokumen' : 'Golive Confirmation',
						fixed: 'right',
						align: 'center',
						render: (_, record) => {
							return (
								<Checkbox
									disabled={
										!record.allowCheck ||
										Boolean(
											financingProductId &&
											financingProductId !== record.financingProduct.id,
										) ||
										Boolean(
											record.disbursementDocumentNo &&
											incompleteDocGroups.has(record.disbursementDocumentNo),
										)
									}
									checked={record.financingProduct.id === financingProductId}
									onClick={() => {
										setFinancingProductId((id) => {
											return id !== record.financingProduct.id
												? record.financingProduct.id
												: undefined;
										});
									}}
								/>
							);
						},
					},
				]}
				pagination={false}
			/>

			<Button
				type="primary"
				htmlType="submit"
				style={{
					display: 'block',
					width: 200,
					marginTop: 40,
					marginLeft: 'auto',
				}}
				disabled={!financingProductId}
				onClick={() => {
					if (!financingProductId) return;
					navigate('VerifyDisbursementPage', {
						mppId,
						financingProductId,
						disbursementId,
						applicationIds: selectedApplicationIds,
						onBack: () => {
							navigate('VerifyDisbursementPOListPage', {
								mppId,
								initialApplicationIds: selectedApplicationIds,
							});
						},
					});
				}}
			>
				VERIFIKASI DOKUMEN
			</Button>
		</div>
	);
};

export default VerifyDisbursementPOListPage;
